import { useState, useCallback,useMemo, useEffect } from 'react'
import axios from 'axios'
import commonService from 'src/service/common'
import { clearSessionAndRedirect, sessionExpiredLogout } from 'src/utils/userLogout'

const _axios = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  ignoreLoading: false,
  timeout: 70000,
  withCredentials: true
});

// Handle 401 responses - clear session and redirect to login
_axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 || error.response?.status === 401) {
      sessionExpiredLogout();
    }
    return Promise.reject(error)
  }
)

const _freedomAxios = axios.create({
  baseURL: process.env.REACT_APP_FREEDOM_URL,
  ignoreLoading: false,
  timeout: 30000
})

const _hrwinStageAxios = axios.create({
  baseURL: process.env.REACT_APP_STAGE_URL,
  ignoreLoading: false,
  timeout: 50000
})

const _opsToolAxios = axios.create({
  baseURL: process.env.REACT_APP_OPSTOOL_URL,
  ignoreLoading: false,
  timeout: 50000
})

const _reimbursementAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  ignoreLoading: false,
  timeout: 50000,
  withCredentials: true
})

const useAxiosLoader = () => {
  const [counter, setCounter] = useState(0);
  const inc = useCallback(() => setCounter(counter => counter + 1), [setCounter]); // add to counter
  const dec = useCallback(() => setCounter(counter => counter - 1), [setCounter]); // remove from counter

  const interceptors = useMemo(() => ({
    request: (config) => {
      if(!config.ignoreLoading){
        inc()
      }
      config.headers = { ...config.headers, authtoken: commonService.getAuthToken() }
      return config
    },
    response: (response) => {
      if(!response.config.ignoreLoading){
        dec()
      }
      if(response.status === 403 || response.status === 401) {
        sessionExpiredLogout();
      }
      return response
    },
    error: (error) => {
      if(!error.config.ignoreLoading){
        dec()
      }
      if(error.response?.status === 403 || error.response?.status === 401) {
        sessionExpiredLogout();
      }
      return Promise.reject(error)
    },
  }), [inc, dec]); // create the interceptors

  useEffect(() => {
    // add request interceptors
    const reqInterceptor = _axios.interceptors.request.use(interceptors.request, interceptors.error);
    // add response interceptors
    const resInterceptor =  _axios.interceptors.response.use(interceptors.response, interceptors.error);
    return () => {
      // remove all intercepts when done
      _axios.interceptors.request.eject(reqInterceptor);
      _axios.interceptors.response.eject(resInterceptor);
    };
  }, [interceptors]);

  return [counter > 0];
};
  
export {
  _axios,
  useAxiosLoader,
  _freedomAxios,
  _hrwinStageAxios,
  _opsToolAxios,
  _reimbursementAxios
}